import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  Receipt as ReceiptIcon,
  Euro as EuroIcon,
  CalendarMonth as CalendarIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../services/api';

const EmployeePayslips = () => {
  const navigate = useNavigate();
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState('');
  
  // Summary stats
  const [stats, setStats] = useState({
    totalEarningsThisYear: 0,
    totalTaxPaidThisYear: 0,
    lastPaymentDate: null,
    averageMonthlyIncome: 0
  });

  useEffect(() => {
    const loggedIn = localStorage.getItem('employeeLoggedIn');
    if (!loggedIn) {
      navigate('/employee/login');
      return;
    }
    fetchPayslips();
  }, [navigate]);

  const fetchPayslips = async () => {
    try {
      const response = await api.get('/employee/payslips');
      setPayslips(response.data.payslips || []);
      setStats(response.data.stats || {
        totalEarningsThisYear: 0,
        totalTaxPaidThisYear: 0,
        lastPaymentDate: null,
        averageMonthlyIncome: 0
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching payslips:', error);
      setPayslips([]);
      setLoading(false);
    }
  };

  const handleView = (payslip) => {
    setSelectedPayslip(payslip);
    setOpenDialog(true);
  };

  const handleDownload = async (payslipId) => {
    try {
      const response = await api.get(`/employee/payslips/${payslipId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip-${payslipId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError('Failed to download payslip');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('lt-LT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const getMonthYear = (month, year) => {
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="fixed" sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => navigate('/employee/dashboard')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            My Payslips
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ pt: 10, pb: 3 }}>
        {/* Alerts */}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EuroIcon sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    This Year Earnings
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {formatCurrency(stats.totalEarningsThisYear)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ReceiptIcon sx={{ color: '#d32f2f', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Tax Paid This Year
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                  {formatCurrency(stats.totalTaxPaidThisYear)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarIcon sx={{ color: '#388e3c', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Last Payment
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#388e3c' }}>
                  {stats.lastPaymentDate ? 
                    new Date(stats.lastPaymentDate).toLocaleDateString() : 
                    'No payments yet'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EuroIcon sx={{ color: '#7b1fa2', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Average Monthly
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#7b1fa2' }}>
                  {formatCurrency(stats.averageMonthlyIncome)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Payslips Table */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Payslip History
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {payslips.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell>Gross Salary</TableCell>
                    <TableCell>Net Salary</TableCell>
                    <TableCell>Tax</TableCell>
                    <TableCell>SODRA</TableCell>
                    <TableCell>Payment Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payslips.map((payslip) => (
                    <TableRow key={payslip.id}>
                      <TableCell>
                        <strong>{getMonthYear(payslip.month, payslip.year)}</strong>
                      </TableCell>
                      <TableCell>{formatCurrency(payslip.gross_salary)}</TableCell>
                      <TableCell>
                        <strong>{formatCurrency(payslip.net_salary)}</strong>
                      </TableCell>
                      <TableCell>{formatCurrency(payslip.income_tax)}</TableCell>
                      <TableCell>
                        {formatCurrency(
                          (payslip.sodra_employee || 0) + 
                          (payslip.health_insurance || 0)
                        )}
                      </TableCell>
                      <TableCell>
                        {payslip.payment_date ? 
                          new Date(payslip.payment_date).toLocaleDateString() : 
                          'Pending'}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={payslip.status || 'Draft'} 
                          color={payslip.status === 'paid' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleView(payslip)}
                            title="View Details"
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDownload(payslip.id)}
                            title="Download PDF"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ReceiptIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
              <Typography color="text.secondary">
                No payslips available yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Your payslips will appear here once they are generated
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>

      {/* Payslip Details Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Payslip Details - {selectedPayslip && getMonthYear(selectedPayslip.month, selectedPayslip.year)}
            </Typography>
            <IconButton onClick={() => setOpenDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPayslip && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Earnings</Typography>
                <Box sx={{ mt: 1 }}>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>Gross Salary:</Typography>
                      <Typography sx={{ fontWeight: 600 }}>
                        {formatCurrency(selectedPayslip.gross_salary)}
                      </Typography>
                    </Box>
                    {selectedPayslip.additions_total > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Bonuses/Additions:</Typography>
                        <Typography>{formatCurrency(selectedPayslip.additions_total)}</Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Deductions</Typography>
                <Box sx={{ mt: 1 }}>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>Income Tax:</Typography>
                      <Typography color="error">
                        -{formatCurrency(selectedPayslip.income_tax)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>SODRA Employee:</Typography>
                      <Typography color="error">
                        -{formatCurrency(selectedPayslip.sodra_employee)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>Health Insurance:</Typography>
                      <Typography color="error">
                        -{formatCurrency(selectedPayslip.health_insurance)}
                      </Typography>
                    </Box>
                    {selectedPayslip.deductions_total > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Other Deductions:</Typography>
                        <Typography color="error">
                          -{formatCurrency(selectedPayslip.deductions_total)}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Divider />
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Net Salary:</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {formatCurrency(selectedPayslip.net_salary)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDownload(selectedPayslip?.id)} startIcon={<DownloadIcon />}>
            Download PDF
          </Button>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeePayslips;
